import _ from "lodash";
import Promise from "bluebird";
import Backbone from "backbone";
import CurationMediaCollection from "scripts/collections/curationMediaCollection";
import CurationMediaCountModel from "scripts/models/curationMediaCountModel";
import PagerView from "scripts/views/pagerView";
import BaseView from "scripts/views/baseView";
import MediaListView from "scripts/views/mediaListView";
import MediaSalvattoreGridView from "scripts/views/mediaSalvattoreGridView";
import StackedCurationMediaTypesChartView from "scripts/views/stackedCurationMediaTypesChartView";
import templateCurationPageContentSubPage from "templates/curationPageContentSubPage.hbs";
import { redirectToFragment } from "scripts/utils/routerHelpers";

const PAGE_SIZE = 100;

class CurationPageMediaSubPageView extends BaseView {
  get template() {
    return templateCurationPageContentSubPage;
  }

  constructor(options) {
    super(options);

    const mediaId = this.model.get("mediaId");

    this.curationMediaCountModel = new CurationMediaCountModel({
      id: mediaId,
    });

    const contentTypeUrlFragment = this.contentTypeUrlFragment(
      this.model.get("contentType"),
    );

    const pageIndex = this.model.get("pageIndex");

    this.collection = new CurationMediaCollection([], {
      path: `/curations/${mediaId}${contentTypeUrlFragment}`,
      mediaId: mediaId,
      state: {
        currentPage: pageIndex,
        pageSize: PAGE_SIZE,
      },
    });

    /**
     * Hack to satisfy Backbone.PageableCollection._checkState
     * if starting from a page other than 0.
     */
    if (pageIndex > 0) {
      this.collection.links[pageIndex] = this.collection.url;
    }

    this.stackedMediaTypesChartView = this.addSubView(
      new StackedCurationMediaTypesChartView({
        model: new Backbone.Model(),
      }),
    );
  }

  createContentView() {
    let contentView;

    let pageViewOptions = {
      hasPreviousPage: this.collection.hasPreviousPage(),
      hasNextPage: this.collection.hasNextPage(),
      pageNumber: this.model.get("pageIndex") + 1,
    };

    if (this.model.get("presentationType") === "list") {
      contentView = new PagerView(
        _.extend(
          {
            mediaView: new MediaListView({
              collection: this.collection,
            }),
          },
          pageViewOptions,
        ),
      );
    } else {
      contentView = new PagerView(
        _.extend(
          {
            mediaView: new MediaSalvattoreGridView({
              collection: this.collection,
            }),
          },
          pageViewOptions,
        ),
      );
    }

    contentView.on(
      "pageSequenceChanged",
      pageNumber => {
        this.model.set("pageIndex", pageNumber - 1);
        redirectToFragment(this.urlFragment());
      },
      this,
    );

    return contentView;
  }

  urlFragment() {
    const mediaId = this.model.get("mediaId");
    const presentationType = this.model.get("presentationType");
    const contentType = this.model.get("contentType");
    const pageNumber = this.model.get("pageIndex") + 1;
    return `/curation/${mediaId}/${presentationType}/${contentType}/${pageNumber}`;
  }

  render() {
    this.$el.html(
      this.template(
        _.merge({}, this.model.toJSON(), this.curationMediaCountModel.toJSON()),
      ),
    );

    this.stackedMediaTypesChartView.model.set(this.mediaTypePercentages());

    this.stackedMediaTypesChartView
      .appendTo(
        $(
          "#bb-curation-page-content-sub-page-stacked-media-types-chart-region",
        ),
      )
      .render();

    this.contentView
      .appendTo($("#bb-curation-page-content-sub-page-content-region"))
      .render();

    this.setActiveFilter();

    return this;
  }

  setActiveFilter() {
    let contentType = this.model.get("contentType");

    if (contentType === "all-media") {
      $("#bb-curation-page-media-sub-page-all-media-link").addClass("active");
    } else {
      this.$el.find("a[href$=" + contentType + "].filter").addClass("active");
    }
  }

  percent(num, divisor) {
    return num / divisor * 100;
  }

  mediaTypePercentages() {
    const allMedia = this.curationMediaCountModel.get("allMediaCount");
    const module = this.curationMediaCountModel.get("moduleCount");
    const anthology = this.curationMediaCountModel.get("anthologyCount");
    const textbook = this.curationMediaCountModel.get("textbookCount");
    const collection = this.curationMediaCountModel.get("collectionCount");
    const chapter = this.curationMediaCountModel.get("chapterCount");

    let written = this.curationMediaCountModel.get("articleCount");
    written += this.curationMediaCountModel.get("bookCount");
    written += this.curationMediaCountModel.get("otherDocumentCount");
    written += this.curationMediaCountModel.get("chapterCount");

    const image = this.curationMediaCountModel.get("imageCount");
    const audio = this.curationMediaCountModel.get("audioCount");
    const video = this.curationMediaCountModel.get("videoCount");

    return {
      modulePercent: this.percent(module, allMedia),
      anthologyPercent: this.percent(anthology + textbook, allMedia),
      collectionPercent: this.percent(collection + chapter, allMedia),
      writtenPercent: this.percent(written, allMedia),
      imagePercent: this.percent(image, allMedia),
      audioPercent: this.percent(audio, allMedia),
      videoPercent: this.percent(video, allMedia),
    };
  }

  sync() {
    return Promise.all([
      this.curationMediaCountModel.fetch(),
      this.syncCollection().then(() => {
        this.contentView = this.addSubView(this.createContentView());
      }),
    ]);
  }

  syncCollection() {
    this.collection.state.currentPage = this.model.get("pageIndex");

    this.collection.queryParams = {
      limit: PAGE_SIZE,
      offset:
        this.collection.state.currentPage * this.collection.state.pageSize,
    };

    return this.collection
      .fetch()
      .cancellable()
      .then(o => o);
  }

  contentTypeUrlFragment(contentType) {
    if (contentType === "all-media") {
      return "/media";
    } else if (
      _.includes(
        ["modules", "anthologies", "textbooks", "collections", "chapters"],
        contentType,
      )
    ) {
      return `/${contentType}`;
    } else if (contentType === "highlights") {
      return "/content?collectionHighlight=1";
    } else {
      return `/content/${contentType}`;
    }
  }
}

export default CurationPageMediaSubPageView;
